/**
 *  @class
 *  @function CollectionTabs
 */

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      setTimeout(() => {
        const buttons = Array.from(this.querySelectorAll('button'));
        const sectionHeader = this.closest('.section-header');
        if (!sectionHeader) return; // Ensure the existence of the section header
        const links = sectionHeader.querySelectorAll('.linked-to-tab');
        this.target = this.dataset.target;
        this.slider = document.getElementById(this.target);
        // Tabs whose products were NOT rendered server-side (homepage HTML
        // diet) — their cards are fetched via the Section Rendering API.
        this.pendingFetches = {};
        this.lazyHandles = new Set(
          ((this.slider && this.slider.dataset.lazyCollections) || '').split(',').filter(Boolean)
        );

        buttons.forEach((button, i) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleButtons(buttons, i);
            this.toggleLinks(links, i);
            // Switching tabs swaps the visible products; reset the carousel to
            // the start and let the arrow nav re-evaluate its disabled state —
            // after any lazy fetch resolves, so scrollWidth is measured on the
            // final card set.
            const finish = () => {
              if (this.slider) {
                this.slider.scrollTo({ left: 0, behavior: 'auto' });
                this.slider.dispatchEvent(new CustomEvent('collection-tabs:changed', { bubbles: true }));
              }
            };
            if (button.dataset.collection) {
              this.toggleCollection(button.dataset.collection).then(finish);
            } else {
              finish();
            }
          });
        });

        // Warm the lazy tabs once the page has settled so the first tab click
        // feels instant. Off the critical path: images stay unloaded until a
        // tab is shown (hidden columns are skipped by lazysizes).
        const prefetch = () => Array.from(this.lazyHandles).forEach((handle) => this.loadCollection(handle));
        if ('requestIdleCallback' in window) {
          requestIdleCallback(prefetch, { timeout: 5000 });
        } else {
          setTimeout(prefetch, 4000);
        }

        if (Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            const index = buttons.indexOf(event.target);
            if (index !== -1) {
              buttons[index].dispatchEvent(new Event('click'));
            }
          });
        }
      }, 10);
    }

    // Fetch a lazy tab's product cards through the Section Rendering API and
    // append them to the carousel. Resolves immediately for tabs already
    // rendered or loaded; concurrent calls share one request.
    loadCollection(handle) {
      if (!this.slider || !this.lazyHandles.has(handle)) return Promise.resolve();
      if (this.pendingFetches[handle]) return this.pendingFetches[handle];
      const rootUrl = window.theme && theme.routes && theme.routes.root_url !== '/' ? theme.routes.root_url : '';
      this.pendingFetches[handle] = fetch(`${rootUrl}/collections/${handle}?section_id=collection-tab-products`)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const cards = Array.from(doc.querySelectorAll('.collection-tab-card'));
          const limit = parseInt(this.slider.dataset.productLimit, 10) || cards.length;
          const columnsClass = this.slider.dataset.columnsClass || 'medium-6 large-4';
          const fragment = document.createDocumentFragment();
          cards.slice(0, limit).forEach((card) => {
            const column = document.createElement('div');
            column.className = `${columnsClass} columns`;
            column.dataset.collection = handle;
            while (card.firstChild) column.appendChild(card.firstChild);
            fragment.appendChild(column);
          });
          this.slider.appendChild(fragment);
          this.lazyHandles.delete(handle);
        })
        .catch(() => {
          // Allow a retry on the next click.
          delete this.pendingFetches[handle];
        });
      return this.pendingFetches[handle];
    }

    toggleButtons(buttons, index) {
      buttons.forEach((button) => button.classList.remove('active'));
      buttons[index].classList.add('active');
    }

    toggleLinks(links, index) {
      if (links.length) {
        links.forEach((link) => link.classList.remove('active'));
        links[index].classList.add('active');
      }
    }

    toggleCollection(handle) {
      const slider = document.getElementById(this.target);
      if (!slider) return Promise.resolve(); // Ensure the existence of the slider
      return this.loadCollection(handle).then(() => {
        const products = slider.querySelectorAll(`.columns:not([data-collection="${handle}"])`);
        const activeProducts = slider.querySelectorAll(`[data-collection="${handle}"]`);

        products.forEach((product) => { product.classList.remove('carousel__slide'); slider.append(product); });
        activeProducts.forEach((product) => product.classList.add('carousel__slide'));
      });
    }
  }
  customElements.define('collection-tabs', CollectionTabs);
}

/**
 *  @class
 *  @function CarouselNav
 *  Desktop-only prev/next arrows that scroll the collection-tabs carousel one
 *  card at a time. Disables the prev arrow at the start and the next arrow at
 *  the end. Mobile keeps native swipe/drag scrolling (arrows hidden via CSS).
 */
if (!customElements.get('carousel-nav')) {
  class CarouselNav extends HTMLElement {
    connectedCallback() {
      setTimeout(() => {
        this.slider = document.getElementById(this.dataset.target);
        this.prevButton = this.querySelector('.collection-tabs__arrow--prev');
        this.nextButton = this.querySelector('.collection-tabs__arrow--next');
        if (!this.slider || !this.prevButton || !this.nextButton) return;

        this.prevButton.addEventListener('click', () => this.scrollByCard(-1));
        this.nextButton.addEventListener('click', () => this.scrollByCard(1));

        // Keep the disabled state in sync with the carousel position.
        this.update = this.update.bind(this);
        this.slider.addEventListener('scroll', this.update, { passive: true });
        this.slider.addEventListener('collection-tabs:changed', this.update);
        window.addEventListener('resize', this.update);

        this.update();
      }, 10);
    }

    // Width of one card including the grid gap, so a click lands cleanly on the
    // next card. Falls back to half the viewport if no slide is measurable.
    cardStep() {
      const slide = this.slider.querySelector('.carousel__slide');
      if (slide) {
        const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
        return slide.getBoundingClientRect().width + gap;
      }
      return this.slider.offsetWidth / 2;
    }

    scrollByCard(direction) {
      // Page two cards at a time.
      this.slider.scrollBy({ left: direction * this.cardStep() * 2, behavior: 'smooth' });
    }

    update() {
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const atStart = this.slider.scrollLeft <= 1;
      const atEnd = this.slider.scrollLeft >= maxScroll - 1;
      this.prevButton.disabled = atStart;
      // No overflow to scroll — nothing to page through, disable both.
      this.nextButton.disabled = atEnd || maxScroll <= 1;
    }
  }
  customElements.define('carousel-nav', CarouselNav);
}